/* eslint-disable @typescript-eslint/no-explicit-any -- Apple Pay SDK has no TypeScript definitions */
import { captureException } from '@sentry/nextjs';
import { buildPaymentDescription } from '@kit/payments/description';

import {
  formatPaymentError,
  safeCompletePayment,
} from './format-payment-error';
import { normalizePhoneNumber } from './phone-utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AddonSelection {
  addonId: string;
  quantity: number;
  expectedUnitPrice?: number;
  bundleItemAddonIds?: string[];
}

export interface ApplePayCourtBooking {
  type: 'court';
  price: number;
  date: Date;
  selectedTime: string;
  selectedDuration: string;
  selectedCourt: string;
  courtName: string;
  templateId: string;
  bookingType: 'court' | 'trainer' | 'ball_machine';
  isPeakTime: boolean;
  membershipId: string | null;
  selectedTrainer?: string;
  selectedBallMachine?: string;
  selectedBookingAccount?: { id: string; isChild: boolean } | null;
  getDurationFromLabel: (label: string) => number;
  calculateEndTime: (startTime: string, durationMinutes: number) => string;
  addons?: AddonSelection[];
  addonsTotalPrice?: number;
  /** Number of participants for trainer/ball_machine bookings (drives per-person fee). */
  numberOfParticipants?: number;
  /** Phase B: auto-applied membership perk (resolved by the slot RPC). */
  memberPerk?: { addonId: string; amountOff: number } | null;
  /** Optional discount code previewed on the booking page. */
  discountCode?: string | null;
  /** Discount amount previewed client-side; server re-computes the canonical value. */
  discountAmountOff?: number;
}

export interface ApplePayClassBooking {
  type: 'class';
  price: number;
  selectedClass: string;
  className: string;
  addons?: AddonSelection[];
  addonsTotalPrice?: number;
  /** Optional discount code previewed on the booking page. */
  discountCode?: string | null;
  /** Discount amount previewed client-side; server re-computes the canonical value. */
  discountAmountOff?: number;
}

export interface ApplePaySessionParams {
  publishableKey: string;
  tenantAccountId: string;
  tenantSlug: string;

  booking: ApplePayCourtBooking | ApplePayClassBooking;

  user: {
    id: string;
    email?: string;
    user_metadata?: { name?: string };
  } | null;
  existingGuestInfo?: {
    userId: string;
    name: string;
    phone: string;
    email: string;
  } | null;

  onSuccess: (redirectUrl: string) => void;
  onGuestCreated?: (
    userId: string,
    name: string,
    phone: string,
    email: string,
  ) => void;
  onError: (message: string) => void;
  onCancel: (message: string) => void;
  onInfo: (message: string) => void;
}

// ---------------------------------------------------------------------------
// Helpers (shared across court & class flows)
// ---------------------------------------------------------------------------

function buildPaymentRequest(
  label: string,
  price: number,
  requireContact: boolean,
) {
  return {
    countryCode: 'SA',
    currencyCode: 'SAR',
    supportedNetworks: ['mada', 'visa', 'masterCard'],
    merchantCapabilities: ['supports3DS', 'supportsDebit', 'supportsCredit'],
    total: { label, amount: price.toFixed(2) },
    ...(requireContact && {
      requiredShippingContactFields: ['name', 'phone', 'email'],
    }),
  };
}

/**
 * Moyasar's /applepay/initiate endpoint rejects any `display_name` containing
 * non-ASCII characters with a 400 ("Invalid display name, only ASCII is
 * supported."). Tenants sometimes decorate class names with glyphs (e.g.
 * "Controlled Chaos ●●●● (Reformer)"), which would otherwise break Apple Pay
 * entirely. Strip everything outside printable ASCII and collapse whitespace.
 * `display_name` is only the merchant label on the Apple Pay sheet, so this is
 * cosmetic — never falls through to an empty string (also a 400).
 */
function toAsciiDisplayName(value: string, fallback: string): string {
  const ascii = value
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return ascii.length > 0 ? ascii : fallback;
}

function setupMerchantValidation(
  session: any,
  publishableKey: string,
  displayName: string,
  bookingType: string,
  onValidated: () => void,
  onError: (msg: string) => void,
) {
  session.onvalidatemerchant = async (event: any) => {
    if (!publishableKey) {
      console.error('Apple Pay: Tenant publishable key not configured');
      onError('Payment configuration error');
      session.abort();
      return;
    }

    const body = {
      validation_url: event.validationURL,
      display_name: toAsciiDisplayName(displayName, 'Booking'),
      domain_name: window.location.hostname,
      publishable_api_key: publishableKey,
    };

    try {
      const response = await fetch(
        'https://api.moyasar.com/v1/applepay/initiate',
        {
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );

      if (!response.ok) {
        // Moyasar returned an error (e.g. invalid publishable key, domain not
        // registered). Passing the error body to completeMerchantValidation
        // would crash Safari, so abort instead.
        const errorBody = await response.json().catch(() => ({}));
        console.error('[Apple Pay] Merchant validation rejected by Moyasar:', {
          status: response.status,
          error: errorBody,
        });
        onError('Apple Pay could not be initialized. Please try card payment.');
        session.abort();
        return;
      }

      const merchantSession = await response.json();
      session.completeMerchantValidation(merchantSession);
      onValidated();
    } catch (error) {
      console.error('Merchant validation error:', error);
      captureException(error, {
        tags: {
          payment_method: 'applepay',
          error_type: 'merchant_validation_failed',
          booking_type: bookingType,
        },
        extra: {
          domain: window.location.hostname,
          hasPublishableKey: !!publishableKey,
        },
      });
      onError('Payment validation failed');
      session.abort();
    }
  };
}

interface GuestInfo {
  userId: string;
  name: string;
  phone: string;
  email: string;
}

/**
 * Extract guest contact from the Apple Pay payment event and create a guest
 * user via server action. Returns `null` when:
 * - the user is already authenticated (no guest needed), OR
 * - guest info was already provided (reuse it)
 *
 * Throws a `GuestExtractionError` with a user-facing message on failure.
 */
class GuestExtractionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GuestExtractionError';
  }
}

async function extractOrReuseGuestInfo(
  event: any,
  isAuthenticated: boolean,
  existing: ApplePaySessionParams['existingGuestInfo'],
): Promise<GuestInfo | null> {
  // Authenticated users don't need guest info
  if (isAuthenticated) return null;

  // Reuse previously created guest
  if (existing?.userId) {
    return {
      userId: existing.userId,
      name: existing.name,
      phone: existing.phone,
      email: existing.email,
    };
  }

  const contact = event.payment.shippingContact;
  const billingContact = event.payment.billingContact;

  if (!contact && !billingContact) {
    throw new GuestExtractionError(
      'Contact information required. Please try another payment method.',
    );
  }

  // Apple Pay contacts can carry a single-letter given name (an initial), or a
  // name that is only whitespace. Those are truthy, so a `name || 'Guest'`
  // fallback lets them through to the server action — where
  // GuestRegistrationSchema's `.min(2)` rejects them and the RSC render throws
  // a bare React #441 at the customer. Require a real name here instead, and
  // tell them how to fix it (same shape as the phone checks below).
  const rawName =
    contact?.givenName && contact?.familyName
      ? `${contact.givenName} ${contact.familyName}`
      : contact?.givenName || billingContact?.givenName || '';

  const name = rawName.trim();

  if (name.length < 2) {
    throw new GuestExtractionError(
      'Please add your full name in Apple Pay settings, or use another payment method.',
    );
  }

  const email = contact?.emailAddress || '';

  const rawPhone = contact?.phoneNumber || billingContact?.phoneNumber || '';

  if (!rawPhone || rawPhone.trim().length === 0) {
    throw new GuestExtractionError(
      'Phone number required. Please try another payment method.',
    );
  }

  const phone = normalizePhoneNumber(rawPhone);

  if (!phone) {
    console.error('[Apple Pay] Phone number too short:', {
      raw: rawPhone,
    });
    throw new GuestExtractionError(
      'Phone number is invalid. Please update your phone number in Apple Pay settings.',
    );
  }

  // Create guest user via server action (dynamic import keeps module lightweight)
  const { createGuestUserFromApplePayAction } =
    await import('../server/guest-registration-actions');

  let guestResult;
  try {
    guestResult = await createGuestUserFromApplePayAction({
      name,
      email,
      phoneNumber: phone,
    });
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Invalid phone number. Please update your phone in Apple Pay settings.';
    throw new GuestExtractionError(errorMessage);
  }

  if (!guestResult.success || !guestResult.data?.userId) {
    const errorMessage =
      'error' in guestResult
        ? (guestResult.error as string)
        : 'Failed to create guest account';
    throw new GuestExtractionError(errorMessage);
  }

  return {
    userId: guestResult.data.userId,
    name,
    phone,
    email,
  };
}

/**
 * Submit a payment to Moyasar and return the parsed response.
 * The `result` object may be empty if the response body is not valid JSON
 * (e.g. a Cloudflare error page). Callers must check `response.ok` first.
 */
async function submitMoyasarPayment(body: Record<string, unknown>) {
  const response = await fetch('https://api.moyasar.com/v1/payments', {
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  // Defensive JSON parse: a proxy or gateway error may return an HTML body.
  const result = await response.json().catch(() => ({
    status: 'failed',
    message: 'Payment service unavailable',
  }));

  return { response, result };
}

function setupCancelHandler(
  session: any,
  state: { merchantValidated: boolean; paymentFailed: boolean },
  bookingType: string,
  publishableKey: string,
  onError: (msg: string) => void,
  onCancel: (msg: string) => void,
) {
  session.oncancel = (event: any) => {
    // When a payment error already occurred in onpaymentauthorized, Apple Pay
    // fires oncancel immediately after STATUS_FAILURE completePayment. Suppress
    // this cancel because the error was already surfaced to the user.
    if (state.paymentFailed) return;

    if (!state.merchantValidated) {
      console.error(
        `[Apple Pay] ${bookingType} payment sheet dismissed before merchant validation`,
        { statusCode: event?.statusCode, domain: window.location.hostname },
      );
      captureException(
        new Error(
          `Apple Pay sheet dismissed: merchant validation failed (${bookingType})`,
        ),
        {
          tags: {
            payment_method: 'applepay',
            error_type: 'session_dismissed_before_validation',
            booking_type: bookingType,
          },
          extra: {
            domain: window.location.hostname,
            hasPublishableKey: !!publishableKey,
          },
        },
      );
      onError('Apple Pay could not be initialized. Please try card payment.');
    } else {
      console.warn(
        `[Apple Pay] ${bookingType} payment sheet dismissed by user`,
      );
      onCancel('Payment cancelled');
    }
  };
}

// ---------------------------------------------------------------------------
// Court flow
// ---------------------------------------------------------------------------

function handleCourtPaymentAuthorized(
  session: any,
  params: ApplePaySessionParams,
  booking: ApplePayCourtBooking,
  state: { paymentFailed: boolean },
) {
  const ApplePaySession = (window as any).ApplePaySession;

  session.onpaymentauthorized = async (event: any) => {
    const token = event.payment.token;
    let bookingId: string | null = null;

    try {
      const guest = await extractOrReuseGuestInfo(
        event,
        !!params.user,
        params.existingGuestInfo,
      );

      if (guest && params.onGuestCreated) {
        params.onGuestCreated(
          guest.userId,
          guest.name,
          guest.phone,
          guest.email,
        );
      }

      const userId = params.user?.id || guest?.userId;
      const userEmail = params.user?.email || guest?.email;

      if (!userId) {
        params.onError('User identification failed');
        safeCompletePayment(session, ApplePaySession.STATUS_FAILURE);
        return;
      }

      // Format date
      const year = booking.date.getFullYear();
      const month = String(booking.date.getMonth() + 1).padStart(2, '0');
      const day = String(booking.date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      const duration = booking.getDurationFromLabel(booking.selectedDuration);
      const endTime = booking.calculateEndTime(booking.selectedTime, duration);

      // Build checkout data
      const checkoutData = {
        courtId: booking.selectedCourt,
        bookingDate: formattedDate,
        startTime: booking.selectedTime,
        endTime,
        slotDuration: duration,
        totalAmount: booking.price,
        returnUrl: `${window.location.origin}/book/return`,
        addons: booking.addons,
        // Phase B: pass the auto-applied perk so the booking-creation
        // action can bake in a matching court_booking_addons discount row.
        memberPerk: booking.memberPerk ?? undefined,
        // Optional discount code from the booking page; the server re-validates
        // and inserts the redemption row, returning the canonical amount off.
        discountCode: booking.discountCode || undefined,
        customerEmail: userEmail || undefined,
        isPeak: booking.isPeakTime,
        ...(params.user &&
          booking.selectedBookingAccount?.isChild && {
            bookingForUserId: booking.selectedBookingAccount.id,
          }),
        metadata: {
          court_name: booking.courtName,
          template_id: booking.templateId,
          ...(booking.membershipId && {
            membership_id: booking.membershipId,
          }),
          ...((booking.numberOfParticipants ?? 1) > 1 && {
            number_of_participants: String(booking.numberOfParticipants),
          }),
          ...(booking.bookingType === 'trainer' && {
            booking_type: 'trainer' as const,
            trainer_id: booking.selectedTrainer,
          }),
          ...(booking.bookingType === 'ball_machine' && {
            booking_type: 'ball_machine' as const,
            trainer_id: booking.selectedBallMachine,
          }),
        },
        provider: 'moyasar' as const,
        usePackage: false,
        useWallet: false,
        paymentMethod: 'card' as const,
        packageType: booking.bookingType as 'court' | 'trainer',
      };

      // Create booking — guest vs authenticated
      const bookingResult = guest
        ? await (async () => {
            const { createGuestCourtBookingCheckoutActionV2 } =
              await import('../server/court-booking/court-booking-actions-v2');
            return createGuestCourtBookingCheckoutActionV2({
              ...checkoutData,
              customerName: guest.name,
              customerPhone: guest.phone,
              guestUserId: guest.userId,
              paymentMethod: 'card' as const,
            });
          })()
        : await (async () => {
            const { createCourtBookingWithPackageAction } =
              await import('../server/court-booking/court-booking-with-package');
            return createCourtBookingWithPackageAction(checkoutData);
          })();

      if (!bookingResult.success || !bookingResult.data) {
        const bookingError =
          ('error' in bookingResult ? bookingResult.error : null) ||
          'Booking creation failed';
        console.error('[Apple Pay] Booking creation failed:', bookingError);
        captureException(
          new Error(`Apple Pay booking creation failed: ${bookingError}`),
          {
            tags: {
              payment_method: 'applepay',
              error_type: 'booking_creation_failed',
            },
            extra: {
              courtId: booking.selectedCourt,
              bookingDate: formattedDate,
              startTime: booking.selectedTime,
              bookingError,
            },
          },
        );
        params.onError(bookingError as string);
        safeCompletePayment(session, ApplePaySession.STATUS_FAILURE);
        return;
      }

      bookingId =
        'bookingId' in bookingResult.data
          ? (bookingResult.data.bookingId as string)
          : null;

      if (!bookingId) {
        params.onError('Booking ID not found');
        safeCompletePayment(session, ApplePaySession.STATUS_FAILURE);
        return;
      }

      if (
        !('provider' in bookingResult.data) ||
        bookingResult.data.provider !== 'moyasar' ||
        !('publishableKey' in bookingResult.data) ||
        typeof bookingResult.data.publishableKey !== 'string' ||
        !bookingResult.data.publishableKey.trim()
      ) {
        throw new Error('Payment configuration error. Please try card payment.');
      }

      // Pull the server-confirmed discount amount (canonical) and fall back to
      // the client-previewed amount only when the action did not return one
      // (older deploys). This is what we charge through Moyasar.
      const serverDiscountAmountOff =
        'discountAmountOff' in bookingResult.data &&
        typeof bookingResult.data.discountAmountOff === 'number'
          ? bookingResult.data.discountAmountOff
          : (booking.discountAmountOff ?? 0);

      const grossAmount = booking.price + (booking.addonsTotalPrice ?? 0);
      const chargeAmount = Math.max(0, grossAmount - serverDiscountAmountOff);

      // Submit payment to Moyasar
      const { response: paymentResponse, result: paymentResult } =
        await submitMoyasarPayment({
          amount: Math.round(chargeAmount * 100),
          currency: 'SAR',
          description: buildPaymentDescription({
            surface: 'court_booking',
            name: booking.courtName,
          }),
          publishable_api_key: bookingResult.data.publishableKey,
          source: { type: 'applepay', token },
          metadata: {
            type: 'court_booking',
            booking_id: bookingId,
            ...('locationId' in bookingResult.data && bookingResult.data.locationId
              ? { location_id: bookingResult.data.locationId }
              : {}),
            payer_name:
              guest?.name || params.user?.user_metadata?.name || undefined,
            tenant_id: params.tenantAccountId,
            tenant_name: params.tenantSlug,
          },
        });

      if (!paymentResponse.ok || paymentResult.status !== 'paid') {
        const rawReason =
          paymentResult.message ||
          paymentResult.source?.message ||
          'Payment failed';
        const failureReason = formatPaymentError(rawReason);
        state.paymentFailed = true;
        console.error('[Apple Pay] Court booking payment failed:', {
          status: paymentResponse.status,
          paymentStatus: paymentResult.status,
          message: paymentResult.message,
          sourceMessage: paymentResult.source?.message,
          sourceType: paymentResult.source?.type,
          bookingId,
        });
        captureException(
          new Error(`Apple Pay court payment failed: ${rawReason}`),
          {
            tags: {
              provider: 'moyasar',
              payment_method: 'applepay',
              error_type: 'payment_failed',
            },
            extra: {
              httpStatus: paymentResponse.status,
              paymentStatus: paymentResult.status,
              message: paymentResult.message,
              sourceMessage: paymentResult.source?.message,
              bookingId,
            },
          },
        );
        params.onError(failureReason);
        safeCompletePayment(session, ApplePaySession.STATUS_FAILURE);

        // Cancel the pending booking so the slot is released
        import('../server/court-booking/check-moyasar-payment').then(
          ({ cancelBookingOnPaymentFailureAction }) =>
            cancelBookingOnPaymentFailureAction({
              bookingId: bookingId!,
              reason: rawReason,
            }).catch(() => {}),
        );
        return;
      }

      // Payment successful
      safeCompletePayment(session, ApplePaySession.STATUS_SUCCESS);
      params.onSuccess(`/payment/${bookingId}?id=${paymentResult.id}`);
    } catch (error) {
      const errorMessage =
        error instanceof GuestExtractionError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Payment processing failed';
      state.paymentFailed = true;
      console.error('[Apple Pay] Court booking error:', errorMessage, error);

      if (!(error instanceof GuestExtractionError)) {
        captureException(error, {
          tags: {
            payment_method: 'applepay',
            error_type: 'court_booking_error',
          },
        });
      }

      params.onError(errorMessage);
      safeCompletePayment(session, ApplePaySession.STATUS_FAILURE);

      // Cancel the pending booking so the slot is released
      if (bookingId) {
        const bookingIdToCancel = bookingId;
        import('../server/court-booking/check-moyasar-payment').then(
          ({ cancelBookingOnPaymentFailureAction }) =>
            cancelBookingOnPaymentFailureAction({
              bookingId: bookingIdToCancel,
              reason: errorMessage,
            }).catch(() => {}),
        );
      }
    }
  };
}

// ---------------------------------------------------------------------------
// Class flow
// ---------------------------------------------------------------------------

function handleClassPaymentAuthorized(
  session: any,
  params: ApplePaySessionParams,
  booking: ApplePayClassBooking,
  state: { paymentFailed: boolean },
) {
  const ApplePaySession = (window as any).ApplePaySession;

  session.onpaymentauthorized = async (event: any) => {
    const token = event.payment.token;

    try {
      const guest = await extractOrReuseGuestInfo(
        event,
        !!params.user,
        params.existingGuestInfo,
      );

      if (guest && params.onGuestCreated) {
        params.onGuestCreated(
          guest.userId,
          guest.name,
          guest.phone,
          guest.email,
        );
      }

      // Guests must have a userId at this point
      if (!params.user && !guest?.userId) {
        safeCompletePayment(session, ApplePaySession.STATUS_FAILURE);
        params.onError('Guest identification failed');
        return;
      }

      // Enroll and create checkout session
      const { createClassEnrollmentCheckoutAction } =
        await import('../server/class-enrollment-checkout-actions');

      const enrollResult = await createClassEnrollmentCheckoutAction({
        bookingId: booking.selectedClass,
        guestUserId: params.user ? undefined : guest?.userId,
        customerEmail: params.user ? undefined : guest?.email || undefined,
        customerName: params.user ? undefined : guest?.name || 'Guest',
        customerPhone: params.user ? undefined : guest?.phone || undefined,
        returnUrl: `${window.location.origin}/book/return`,
        addons: booking.addons,
        // Optional discount code from the booking page; the server re-validates
        // and inserts the participant-scoped redemption row, returning the
        // canonical amount off.
        discountCode: booking.discountCode || undefined,
      });

      if (!enrollResult.success || !enrollResult.data) {
        params.onError(
          (enrollResult as { error?: string }).error ||
            'Failed to enroll in class',
        );
        safeCompletePayment(session, ApplePaySession.STATUS_FAILURE);
        return;
      }

      const {
        participantId,
        publishableKey,
        price,
        className: enrolledClassName,
      } = enrollResult.data;

      // Pull the server-confirmed discount amount (canonical) and fall back to
      // the client-previewed amount only when the action did not return one.
      // Mirrors the court Apple Pay flow.
      const serverDiscountAmountOff =
        'discountAmountOff' in enrollResult.data &&
        typeof enrollResult.data.discountAmountOff === 'number'
          ? enrollResult.data.discountAmountOff
          : (booking.discountAmountOff ?? 0);

      const chargeAmount = Math.max(
        0,
        price + (booking.addonsTotalPrice ?? 0) - serverDiscountAmountOff,
      );

      // Seat fully covered by a granted discount: the server already confirmed +
      // cash_paid it, so skip the 0 charge that would fail and orphan the row.
      const alreadyPaid =
        'paid' in enrollResult.data && enrollResult.data.paid === true;
      if (alreadyPaid && chargeAmount <= 0) {
        safeCompletePayment(session, ApplePaySession.STATUS_SUCCESS);
        params.onSuccess(`/payment/${participantId}?type=class`);
        return;
      }

      // Submit payment to Moyasar
      const { response: paymentResponse, result: paymentResult } =
        await submitMoyasarPayment({
          amount: Math.round(chargeAmount * 100),
          currency: 'SAR',
          description: buildPaymentDescription({
            surface: 'class_registration',
            name: enrolledClassName,
            isAddon: alreadyPaid,
          }),
          publishable_api_key: publishableKey,
          source: { type: 'applepay', token },
          metadata: {
            type: 'class_registration',
            isClassPayment: 'true',
            participantId,
            booking_id: enrollResult.data.bookingId,
            // Seat already settled server-side (fully-granted/free): this charge
            // is add-ons only — flag it so settlement doesn't re-run the class fee.
            ...(alreadyPaid && { is_addon_payment: 'true' }),
            payer_name:
              guest?.name ||
              params.user?.user_metadata?.name ||
              params.user?.email ||
              undefined,
            tenant_id: enrollResult.data.tenantId || params.tenantAccountId,
            tenant_name: enrollResult.data.tenantName || params.tenantSlug,
          },
        });

      if (!paymentResponse.ok || paymentResult.status !== 'paid') {
        const rawReason =
          paymentResult.message ||
          paymentResult.source?.message ||
          'Payment failed';
        const failureReason = formatPaymentError(rawReason);
        state.paymentFailed = true;
        console.error('[Apple Pay] Class payment failed:', {
          status: paymentResponse.status,
          paymentStatus: paymentResult.status,
          message: paymentResult.message,
          sourceMessage: paymentResult.source?.message,
          sourceType: paymentResult.source?.type,
        });
        captureException(
          new Error(`Apple Pay class payment failed: ${rawReason}`),
          {
            tags: {
              provider: 'moyasar',
              payment_method: 'applepay',
              error_type: 'payment_failed',
              booking_type: 'class',
            },
            extra: {
              httpStatus: paymentResponse.status,
              paymentStatus: paymentResult.status,
              message: paymentResult.message,
              sourceMessage: paymentResult.source?.message,
            },
          },
        );
        params.onError(failureReason);
        safeCompletePayment(session, ApplePaySession.STATUS_FAILURE);
        return;
      }

      // Payment successful
      safeCompletePayment(session, ApplePaySession.STATUS_SUCCESS);
      params.onSuccess(
        `/payment/${participantId}?type=class&id=${paymentResult.id}`,
      );
    } catch (error) {
      const errorMessage =
        error instanceof GuestExtractionError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Payment processing failed';
      state.paymentFailed = true;
      console.error('[Apple Pay] Class payment error:', errorMessage, error);

      if (!(error instanceof GuestExtractionError)) {
        captureException(error, {
          tags: {
            payment_method: 'applepay',
            error_type: 'class_payment_error',
          },
        });
      }

      params.onError(errorMessage);
      safeCompletePayment(session, ApplePaySession.STATUS_FAILURE);
    }
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create and begin an Apple Pay session for either a court booking or class
 * registration. This function **must** be called synchronously from a user
 * gesture handler (click/tap) — Safari requires the `ApplePaySession` to be
 * constructed on the same call-stack as the user interaction.
 */
export function initiateApplePaySession(params: ApplePaySessionParams): void {
  const ApplePaySessionCtor = (window as any).ApplePaySession;
  const { booking } = params;

  const label =
    booking.type === 'class' ? booking.className : booking.courtName;

  const grossPrice = booking.price + (booking.addonsTotalPrice ?? 0);
  const previewedDiscount = booking.discountAmountOff ?? 0;
  const totalPrice = Math.max(0, grossPrice - previewedDiscount);

  const paymentRequest = buildPaymentRequest(label, totalPrice, !params.user);

  // Session MUST be created synchronously (user gesture requirement)
  const session = new ApplePaySessionCtor(5, paymentRequest);

  // Mutable state shared between handlers
  const state = { merchantValidated: false, paymentFailed: false };

  // Merchant validation (shared)
  const displayName =
    booking.type === 'class' ? booking.className : 'Court Booking';
  const bookingTypeTag = booking.type === 'class' ? 'class' : 'court';

  setupMerchantValidation(
    session,
    params.publishableKey,
    displayName,
    bookingTypeTag,
    () => {
      state.merchantValidated = true;
    },
    params.onError,
  );

  // Payment authorization (flow-specific)
  if (booking.type === 'court') {
    handleCourtPaymentAuthorized(session, params, booking, state);
  } else {
    handleClassPaymentAuthorized(session, params, booking, state);
  }

  // Cancel handler (shared)
  setupCancelHandler(
    session,
    state,
    bookingTypeTag,
    params.publishableKey,
    params.onError,
    params.onCancel,
  );

  session.begin();
}
